import React from 'react';
import axios from 'axios';
import { checkForAuthorization } from 'utils/index';

// Enabling Auto-Shortlist queues one background job per invited startup and the toggle
// response carries no batch id, so there is no way to ask "is it finished?".
//
// This polls the kanban board's per-column counts - a cheap read whose result is never
// rendered - until they stop changing, then calls onComplete ONCE. The view therefore
// updates a single time with the final result instead of flashing through every
// intermediate state. The probe deliberately uses plain axios rather than the views'
// own react-query hooks: those would flip their loading flags and repaint the skeleton,
// which is the flicker this exists to avoid.
//
// Limit worth knowing: a queue that goes quiet for longer than
// STABLE_ROUNDS * POLL_INTERVAL_MS mid-run reads as finished and commits early.
const POLL_INTERVAL_MS = 3000;
const STABLE_ROUNDS = 2;
const MAX_RUN_MS = 5 * 60 * 1000;

// How many startups sit in the New column right now. Enabling Auto-Shortlist with an
// empty New column queues nothing, so the caller can skip the progress snackbar.
// Returns null when it cannot tell - callers should assume there IS work.
export const fetchInvitedCount = async ({ investor_id, pipelineId, statusId }) => {
  if (!investor_id || !statusId) return null;
  try {
    const pipelineParam = pipelineId && pipelineId !== 'All' ? `&investor_pipeline_id=${pipelineId}` : '';
    const res = await axios({
      method: 'get',
      url: `${process.env.REACT_APP_BASE_URL}/kanban/board/${investor_id}?limit=1${pipelineParam}`,
      headers: { Authorization: `Bearer ${checkForAuthorization()}` },
    });
    const column = (res?.data?.data?.columns || []).find((c) => String(c?._id) === String(statusId));
    return Number(column?.count ?? 0);
  } catch (e) {
    return null;
  }
};

export const useAutoShortlistRefresh = ({ investor_id, pipelineId, onComplete }) => {
  const [isRunning, setIsRunning] = React.useState(false);

  // Callers pass inline functions that change every render; keep the latest.
  const onCompleteRef = React.useRef(onComplete);
  React.useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  const argsRef = React.useRef({ investor_id, pipelineId });
  React.useEffect(() => { argsRef.current = { investor_id, pipelineId }; }, [investor_id, pipelineId]);

  const timerRef = React.useRef(null);
  const lastSigRef = React.useRef(undefined);
  const stableRef = React.useRef(0);
  const startedAtRef = React.useRef(0);
  const cancelledRef = React.useRef(false);

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const stop = React.useCallback(() => {
    cancelledRef.current = true;
    clearTimer();
    setIsRunning(false);
  }, []);

  // limit=1 keeps the payload tiny; `count` is each column's total, not the number of
  // cards returned, so the signature stays accurate.
  const probe = async () => {
    const { investor_id: id, pipelineId: pid } = argsRef.current;
    const pipelineParam = pid && pid !== 'All' ? `&investor_pipeline_id=${pid}` : '';
    const res = await axios({
      method: 'get',
      url: `${process.env.REACT_APP_BASE_URL}/kanban/board/${id}?limit=1${pipelineParam}`,
      headers: { Authorization: `Bearer ${checkForAuthorization()}` },
    });
    return (res?.data?.data?.columns || []).map((c) => `${c?._id}:${c?.count}`).join('|');
  };

  const tick = React.useCallback(async () => {
    let sig;
    try {
      sig = await probe();
    } catch (e) {
      // A failed probe counts as "no change" rather than aborting - a blip should not
      // strand the run, and MAX_RUN_MS still bounds it.
      sig = lastSigRef.current;
    }
    if (cancelledRef.current) return;

    // undefined on the first tick: nothing observed yet, so just record it.
    if (lastSigRef.current !== undefined && sig === lastSigRef.current) {
      stableRef.current += 1;
    } else {
      stableRef.current = 0;
      lastSigRef.current = sig;
    }

    const expired = Date.now() - startedAtRef.current > MAX_RUN_MS;
    if (stableRef.current >= STABLE_ROUNDS || expired) {
      clearTimer();
      setIsRunning(false);
      if (onCompleteRef.current) onCompleteRef.current();
      return;
    }

    timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
  }, []);

  // `delayMs` mirrors the investor's auto_shortlist.delay_ms - jobs carry that Bull
  // delay, so nothing can move before it elapses.
  const start = React.useCallback((delayMs = 0) => {
    clearTimer();
    cancelledRef.current = false;
    lastSigRef.current = undefined;
    stableRef.current = 0;
    startedAtRef.current = Date.now();
    setIsRunning(true);
    timerRef.current = setTimeout(tick, Math.max(0, Number(delayMs) || 0));
  }, [tick]);

  React.useEffect(() => clearTimer, []);

  return { start, stop, isRunning };
};

export default useAutoShortlistRefresh;
